<script setup lang="ts">
import { Trash2 } from "@lucide/vue";
import { computed, inject } from "vue";
import type {
  PopupEmbedSettings,
  PopupScheduleDayPreset,
  PopupScheduleSlot,
} from "@aswin.dev/types";
import { POPUP_SCHEDULE_TIMEZONES } from "@aswin.dev/types";
import { useI18n } from "../composables/useI18n";
import { EDITOR_KEY } from "../keys";
import type { BaseEditorReturn } from "../composables/useEditorCore";
import { resolvePopupEmbed } from "../utils/resolvePopupEmbed";
import PopupDesignSwitch from "./PopupDesignSwitch.vue";
import PopupScheduleDatePicker from "./PopupScheduleDatePicker.vue";
import { hustlepopsHelpUrl } from "../constants/hustlepopsHelp";

const props = withDefaults(
  defineProps<{ layout?: "standalone" | "popupAdjacent" }>(),
  { layout: "standalone" },
);

const editor = inject(EDITOR_KEY) as BaseEditorReturn | null;
if (!editor) {
  throw new Error("PopupScheduleView requires EDITOR_KEY");
}

const { t } = useI18n();

const rootClass = computed(() =>
  props.layout === "popupAdjacent"
    ? "tpl-popup-schedule tpl:w-full tpl:overflow-x-hidden tpl:px-4 tpl:pb-6 tpl:pt-4 tpl:bg-[var(--tpl-bg)]"
    : "tpl-popup-schedule tpl:mx-auto tpl:w-full tpl:max-w-3xl tpl:px-6 tpl:py-8 tpl:pb-14 tpl:bg-[var(--tpl-bg)]",
);

const popup = computed(() => resolvePopupEmbed(editor!.content.value.settings));

const selectClass =
  "tpl:w-full tpl:min-w-0 tpl:rounded-lg tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:px-3 tpl:py-2.5 tpl:text-sm tpl:text-[var(--tpl-text)] tpl:outline-none focus-visible:tpl:ring-2 focus-visible:tpl:ring-[var(--tpl-primary)] focus-visible:tpl:ring-offset-2 focus-visible:tpl:ring-offset-[var(--tpl-bg)]";

const labelClass =
  "tpl:block tpl:mb-1.5 tpl:text-[13px] tpl:leading-snug tpl:text-[var(--tpl-text-muted)]";

function patchSchedule(patch: Partial<PopupEmbedSettings["schedule"]>): void {
  const cur = popup.value;
  const schedule = { ...cur.schedule, ...patch };
  editor!.updateSettings({ popup: { ...cur, schedule } } as Parameters<
    BaseEditorReturn["updateSettings"]
  >[0]);
}

function replaceSlots(slots: PopupScheduleSlot[]): void {
  patchSchedule({ slots });
}

function updateSlot(id: string, patch: Partial<PopupScheduleSlot>): void {
  replaceSlots(
    popup.value.schedule.slots.map((s) =>
      s.id === id ? { ...s, ...patch } : s,
    ),
  );
}

function removeSlot(id: string): void {
  replaceSlots(popup.value.schedule.slots.filter((s) => s.id !== id));
}

function addSlot(): void {
  const id =
    globalThis.crypto?.randomUUID?.() ?? `slot-${Date.now().toString(36)}`;
  replaceSlots([
    ...popup.value.schedule.slots,
    {
      id,
      dayPreset: "everyday",
      startTime: "00:00",
      endTime: "23:59",
    },
  ]);
}

const quarterHourTimes = computed(() => {
  const out: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 15, 30, 45]) {
      out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  if (!out.includes("23:59")) out.push("23:59");
  return out;
});

const timezoneChoices = computed(() => {
  const cur = popup.value.schedule.timeZone;
  const known = POPUP_SCHEDULE_TIMEZONES.some((z) => z.id === cur);
  if (known) return [...POPUP_SCHEDULE_TIMEZONES];
  return [{ id: cur, label: cur }, ...POPUP_SCHEDULE_TIMEZONES];
});

const dayOptions = computed(() =>
  (
    [
      "everyday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ] as PopupScheduleDayPreset[]
  ).map((id) => ({
    id,
    label:
      t.popupSchedule.dayLabels[id as keyof typeof t.popupSchedule.dayLabels],
  })),
);

function onTimezoneChange(e: Event): void {
  patchSchedule({
    timeZone: (e.target as HTMLSelectElement).value,
  });
}

function onSlotDayChange(id: string, e: Event): void {
  updateSlot(id, {
    dayPreset: (e.target as HTMLSelectElement).value as PopupScheduleDayPreset,
  });
}

function onSlotStartChange(id: string, e: Event): void {
  updateSlot(id, { startTime: (e.target as HTMLSelectElement).value });
}

function onSlotEndChange(id: string, e: Event): void {
  updateSlot(id, { endTime: (e.target as HTMLSelectElement).value });
}
</script>

<template>
  <div :class="rootClass">
    <div class="tpl:mb-5">
      <label class="tpl:sr-only" for="popup-schedule-timezone">{{
        t.popupSchedule.timeZone
      }}</label>
      <span :class="labelClass">{{ t.popupSchedule.timeZone }}</span>
      <select
        id="popup-schedule-timezone"
        :class="selectClass"
        :value="popup.schedule.timeZone"
        @change="onTimezoneChange"
      >
        <option v-for="z in timezoneChoices" :key="z.id" :value="z.id">
          {{ z.label }}
        </option>
      </select>
    </div>

    <div class="tpl:mb-3 tpl:grid tpl:grid-cols-2 tpl:gap-3">
      <div>
        <label class="tpl:sr-only" for="popup-schedule-start-date">{{
          t.popupSchedule.startDate
        }}</label>
        <span :class="labelClass">{{ t.popupSchedule.startDate }}</span>
        <PopupScheduleDatePicker
          input-id="popup-schedule-start-date"
          :model-value="popup.schedule.startDate"
          :placeholder="t.popupSchedule.startImmediate"
          :clear-label="t.popupSchedule.clearStartDate"
          @update:model-value="patchSchedule({ startDate: $event })"
        />
      </div>
      <div>
        <label class="tpl:sr-only" for="popup-schedule-end-date">{{
          t.popupSchedule.endDate
        }}</label>
        <span :class="labelClass">{{ t.popupSchedule.endDate }}</span>
        <PopupScheduleDatePicker
          input-id="popup-schedule-end-date"
          :model-value="popup.schedule.endDate"
          :placeholder="t.popupSchedule.endNone"
          :clear-label="t.popupSchedule.clearEndDate"
          @update:model-value="patchSchedule({ endDate: $event })"
        />
      </div>
    </div>

    <a
      :href="hustlepopsHelpUrl.schedule"
      target="_blank"
      rel="noopener noreferrer"
      class="tpl:mb-6 tpl:inline-block tpl:text-[13px] tpl:underline tpl:text-[var(--tpl-text-muted)] hover:tpl:text-[var(--tpl-text)]"
    >
      {{ t.popupSchedule.needHelp }}
    </a>

    <div class="tpl:mb-5 tpl:flex tpl:items-center tpl:gap-3">
      <PopupDesignSwitch
        tone="neutral"
        :checked="popup.schedule.detailedScheduleEnabled"
        @toggle="
          patchSchedule({
            detailedScheduleEnabled: !popup.schedule.detailedScheduleEnabled,
          })
        "
      />
      <span class="tpl:text-sm tpl:leading-snug tpl:text-[var(--tpl-text)]">{{
        t.popupSchedule.detailedSchedule
      }}</span>
    </div>

    <template v-if="popup.schedule.detailedScheduleEnabled">
      <p :class="`${labelClass} tpl:mb-2 tpl:mt-1`">
        {{ t.popupSchedule.detailedScheduleHeading }}
      </p>

      <div class="tpl:space-y-2 tpl:mb-4">
        <div
          v-for="slot in popup.schedule.slots"
          :key="slot.id"
          class="tpl:grid tpl:grid-cols-[auto_minmax(0,1fr)_4.5rem_4.5rem] tpl:items-center tpl:gap-2"
        >
          <button
            type="button"
            class="tpl:flex tpl:h-9 tpl:w-[26px] tpl:cursor-pointer tpl:items-center tpl:justify-center tpl:rounded-md tpl:border-none tpl:bg-transparent tpl:text-[var(--tpl-text-muted)] hover:tpl:bg-[var(--tpl-bg-hover)] hover:tpl:text-[var(--tpl-text)] focus-visible:tpl:outline focus-visible:tpl:outline-2 focus-visible:tpl:outline-[var(--tpl-primary)]"
            :aria-label="t.popupSchedule.removeSlot"
            @click="removeSlot(slot.id)"
          >
            <Trash2 :size="16" :stroke-width="1.75" aria-hidden="true" />
          </button>
          <select
            :class="selectClass"
            :value="slot.dayPreset"
            @change="onSlotDayChange(slot.id, $event)"
          >
            <option v-for="opt in dayOptions" :key="opt.id" :value="opt.id">
              {{ opt.label }}
            </option>
          </select>
          <select
            :class="selectClass"
            :value="slot.startTime"
            @change="onSlotStartChange(slot.id, $event)"
          >
            <option
              v-for="tm in quarterHourTimes"
              :key="`s-${slot.id}-${tm}`"
              :value="tm"
            >
              {{ tm }}
            </option>
          </select>
          <select
            :class="selectClass"
            :value="slot.endTime"
            @change="onSlotEndChange(slot.id, $event)"
          >
            <option
              v-for="tm in quarterHourTimes"
              :key="`e-${slot.id}-${tm}`"
              :value="tm"
            >
              {{ tm }}
            </option>
          </select>
        </div>
      </div>

      <button
        type="button"
        class="tpl:inline-flex tpl:cursor-pointer tpl:items-center tpl:justify-center tpl:rounded-lg tpl:border tpl:border-[var(--tpl-text)] tpl:bg-transparent tpl:px-4 tpl:py-2.5 tpl:text-sm tpl:font-medium tpl:text-[var(--tpl-text)] hover:tpl:bg-[var(--tpl-bg-hover)] focus-visible:tpl:outline focus-visible:tpl:outline-2 focus-visible:tpl:outline-offset-2 focus-visible:tpl:outline-[var(--tpl-primary)]"
        @click="addSlot"
      >
        {{ t.popupSchedule.addTimeSlot }}
      </button>
    </template>
  </div>
</template>
